import React from "react";
import InputBox from "../Components/InputBox";
import SelectInput from "../Components/SelectInput";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { addNewDoc } from "../../../utils/firebase/Firebase.utils";
import { motion } from "framer-motion";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";
import {
  FiX,
  FiCheckCircle,
  FiCalendar,
  FiFlag,
  FiFileText,
  FiLoader,
  FiPlus,
  FiUsers,
} from "react-icons/fi";

export default function TodosForm({ setAddTodo }) {
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = React.useState({});
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [assignedToArray, setAssignedToArray] = React.useState([]);

  const priorityOptions = [
    { value: "high", label: "High Priority", icon: "🔴" },
    { value: "medium", label: "Medium Priority", icon: "🟡" },
    { value: "low", label: "Low Priority", icon: "🟢" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveTodo = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const docObject = {
        ...formData,
        createdBy: currentUser.email,
        user: currentUser.uid,
        status: "pending",
        createdAt: new Date().toISOString(),
        assignedTo: assignedToArray,
        history: [
          {
            action: "Task Created",
            by: currentUser.email,
            at: new Date().toLocaleString(),
          },
          ...(assignedToArray.length > 0
            ? [
                {
                  action: "Task Assigned to",
                  by: currentUser.email,
                  to: assignedToArray.map((user) => user.label).join(", "),
                  at: new Date().toLocaleString(),
                },
              ]
            : []),
        ],
      };
      await addNewDoc("todos", docObject);
      toast.success("Task added successfully");
      setAddTodo(false);
    } catch (error) {
      toast.error("Failed to add task");
      console.error("Error adding task:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden max-w-3xl w-full mx-auto text-left border border-gray-100"
    >
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
        <div className="text-[#363062]">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FiPlus className="w-5 h-5 text-[#8FCD00]" />
            New Task
          </h2>
          <p className="text-sm text-gray-500">Create a new task to track</p>
        </div>
        <button
          onClick={() => setAddTodo(false)}
          className="p-2 rounded-full hover:bg-gray-50 transition-colors"
        >
          <FiX className="w-5 h-5 text-gray-400 hover:text-[#363062]" />
        </button>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSaveTodo} className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-5">
            {/* Task Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#363062] flex items-center gap-2">
                <FiCheckCircle className="w-4 h-4 text-[#8FCD00]" />
                Task Name
              </label>
              <input
                name="name"
                type="text"
                placeholder="Enter task name"
                required
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] transition-colors"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#363062] flex items-center gap-2">
                <FiFileText className="w-4 h-4 text-[#8FCD00]" />
                Description
              </label>
              <textarea
                name="description"
                placeholder="Enter task description"
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] transition-colors resize-none"
              />
            </div>

            {/* Due Date & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#363062] flex items-center gap-2">
                  <FiCalendar className="w-4 h-4 text-[#8FCD00]" />
                  Due Date
                </label>
                <input
                  name="dueDate"
                  type="date"
                  required
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] transition-colors"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#363062] flex items-center gap-2">
                  <FiFlag className="w-4 h-4 text-[#8FCD00]" />
                  Priority
                </label>
                <select
                  name="priority"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  required
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] transition-colors appearance-none bg-white"
                >
                  <option value="">Select Priority</option>
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Right Column - Assignment */}
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#363062] flex items-center gap-2">
                <FiUsers className="w-4 h-4 text-[#8FCD00]" />
                Assign Task
              </label>
              <div className="border border-gray-200 rounded-lg p-4">
                <TaskAssignmentModule
                  assignedToArray={assignedToArray}
                  setAssignedToArray={setAssignedToArray}
                  editMode={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-6 mt-6 border-t border-gray-100">
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full px-6 py-3 text-sm font-medium text-white bg-[#363062] rounded-lg hover:bg-[#363062]/90 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:ring-offset-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                Creating Task...
              </>
            ) : (
              <>
                <FiPlus className="w-4 h-4" />
                Create Task
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}